import { useTranslation } from "react-i18next";

import { Group, Progress, SimpleGrid, Table, Text, Title } from "@mantine/core";

import { useFormatters } from "@prostocheck/i18n";
import { Card } from "@prostocheck/ui";

import { IZReportCardProps } from "./z-report-card.models";

/**
 * The tax cabinet shows a Z-report as raw XML-derived text. This is the whole
 * point of the product: the same numbers, readable at a glance.
 */
export const ZReportCard = ({ report, isExpanded }: IZReportCardProps) => {
  const { t } = useTranslation();
  const { formatDateTime, formatNumber, formatPrice } = useFormatters();

  const net = report.salesTotal - report.returnsTotal;
  const turnover = report.payments.cash + report.payments.card;
  const cashShare = turnover ? (report.payments.cash / turnover) * 100 : 0;

  return (
    <Card>
      <Group justify="space-between" align="baseline" gap="xs">
        <Title order={3} size="h5">
          {t("zReport.shift", { number: formatNumber(report.shiftNumber) })}
        </Title>
        <Text size="sm" c="dimmed">
          {formatDateTime(report.openedAt)} — {formatDateTime(report.closedAt)}
        </Text>
      </Group>

      <SimpleGrid component="dl" cols={{ base: 2, sm: 4 }} spacing="md" mt="lg">
        <div>
          <Text component="dt" size="sm" c="dimmed">
            {t("zReport.sales")}
          </Text>
          <Text component="dd" fz="xl" fw={600} mt={4}>
            {formatPrice(report.salesTotal)}
          </Text>
        </div>
        <div>
          <Text component="dt" size="sm" c="dimmed">
            {t("zReport.returns")}
          </Text>
          <Text component="dd" fz="xl" fw={600} mt={4}>
            {formatPrice(report.returnsTotal)}
          </Text>
        </div>
        <div>
          <Text component="dt" size="sm" c="dimmed">
            {t("zReport.net")}
          </Text>
          <Text component="dd" fz="xl" fw={600} mt={4} c="brand.7">
            {formatPrice(net)}
          </Text>
        </div>
        <div>
          <Text component="dt" size="sm" c="dimmed">
            {t("zReport.receiptsCount")}
          </Text>
          <Text component="dd" fz="xl" fw={600} mt={4}>
            {formatNumber(report.salesCount + report.returnsCount)}
          </Text>
        </div>
      </SimpleGrid>

      <Group justify="space-between" gap="xs" mt="lg" mb="xs">
        <Text size="sm" c="dimmed">
          {t("zReport.cash", { amount: formatPrice(report.payments.cash) })}
        </Text>
        <Text size="sm" c="dimmed">
          {t("zReport.card", { amount: formatPrice(report.payments.card) })}
        </Text>
      </Group>
      <Progress
        value={cashShare}
        size="sm"
        color="brand.6"
        bg="brand.1"
        role="img"
        aria-label={t("zReport.paymentsSplit", {
          percent: Math.round(cashShare),
        })}
      />

      {isExpanded && (
        <div className="mt-6 border-t border-ink-900/8 pt-4">
          <Title order={4} size="h6" mb="sm">
            {t("zReport.vatTitle")}
          </Title>

          <Table verticalSpacing="xs" horizontalSpacing={0}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("zReport.vatRate")}</Table.Th>
                <Table.Th>{t("zReport.vatTurnover")}</Table.Th>
                <Table.Th ta="right">{t("zReport.vatAmount")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {report.vat.map(entry => (
                <Table.Tr key={entry.rate}>
                  <Table.Td>
                    {t("zReport.vatRateValue", { rate: entry.rate })}
                  </Table.Td>
                  <Table.Td c="dimmed">{formatPrice(entry.turnover)}</Table.Td>
                  <Table.Td ta="right" fw={500}>
                    {formatPrice(entry.amount)}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>

          <Text size="xs" c="dimmed" mt="md">
            {t("zReport.reportFiscalNumber", {
              fiscalNumber: report.fiscalNumber,
            })}
          </Text>
        </div>
      )}
    </Card>
  );
};
